import { useState, useMemo, useRef, useEffect } from 'react';
import { Link, useLocation, useNavigate } from '@tanstack/react-router';
import { AnimatePresence, motion } from 'motion/react';
import { auth } from '@/core/firebase/config';
import { useTheme, type ThemePreference } from '@/shared/providers/ThemeProvider';
import { useSidebarData } from '@/shared/providers/SidebarProvider';
import useSavePreferredOffice from '@/shared/hooks/useSavePreferredOffice';
import { Skeleton } from '@/shared/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
} from '@/shared/components/ui/select';
import type { Office } from '@/features/office/types';
import { ConfirmDialog } from '@/shared/components/ui/ConfirmDialog';
import { ToggleGroup, ToggleGroupItem } from '@/shared/components/AnimateUi/Radix/ToggleGroup';
import {
  IconBrightnessAuto,
  IconBuilding,
  IconUsers,
  IconChartBar,
  IconUser,
  IconCalendarEvent,
  IconCalendar,
  IconMapPin,
  IconMessage2,
} from '@tabler/icons-react';
import { LogOut, Moon, Sparkles, Sun } from 'lucide-react';
import { cn } from '@/shared/utils/utils';

function SuggestionCountBadge({ count }: { count: number }) {
  if (count <= 0) return null;
  return (
    <span
      aria-hidden
      className="absolute -top-2 -right-2 flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[10px] leading-none font-semibold text-primary-foreground ring-2 ring-sidebar"
    >
      {count > 99 ? '99+' : count}
    </span>
  );
}

interface AppSidebarProps {
  skeleton?: boolean;
  mobile?: boolean;
  hovered?: boolean;
  onNavigate?: () => void;
  onLockHover?: () => void;
  onUnlockHover?: () => void;
}

type RailTone = 'active' | 'open' | 'idle';

function railTone({ active, open }: { active?: boolean; open?: boolean }): RailTone {
  if (active) return 'active';
  if (open) return 'open';
  return 'idle';
}

function railToneClass(tone: RailTone) {
  switch (tone) {
    case 'active':
      return 'bg-gray-200 font-medium text-sidebar-accent-foreground dark:bg-sidebar-accent';
    case 'open':
      return 'bg-gray-200 text-sidebar-foreground dark:bg-sidebar-accent';
    default:
      return 'text-sidebar-foreground/70';
  }
}

function railRowClass({
  active,
  open,
  className,
}: {
  active?: boolean;
  open?: boolean;
  className?: string;
}) {
  const tone = railTone({ active, open });
  return cn(
    'pointer-events-auto ml-auto flex w-max items-center rounded-md text-sm transition-colors',
    railToneClass(tone),
    tone === 'active' && 'hover:bg-gray-200 dark:hover:bg-sidebar-accent',
    tone === 'open' && 'hover:bg-gray-200 dark:hover:bg-sidebar-accent',
    tone === 'idle' &&
      'hover:bg-gray-200/70 hover:text-sidebar-foreground dark:hover:bg-sidebar-accent',
    className
  );
}

function RailLabel({ show, children }: { show: boolean; children: React.ReactNode }) {
  return (
    <span
      aria-hidden
      className={cn(
        'overflow-hidden whitespace-nowrap transition-[max-width,opacity,padding] duration-200 ease-out',
        show ? 'max-w-48 py-2 pr-3 pl-3 opacity-100' : 'max-w-0 p-0 opacity-0'
      )}
    >
      {children}
    </span>
  );
}

function RailIcon({ children }: { children: React.ReactNode }) {
  return <span className="flex w-14 shrink-0 items-center justify-center py-2">{children}</span>;
}

/* ── Skeleton states ── */

function SidebarSkeletonContent() {
  return (
    <div className="flex flex-col h-full w-14 text-sidebar-foreground">
      <div className="h-[72px] flex items-center justify-center shrink-0">
        <Skeleton className="h-8 w-8 rounded-full" />
      </div>
      <div className="flex-1 overflow-y-auto py-4 space-y-6 px-1.5">
        <div>
          <Skeleton className="h-9 w-9 mx-auto rounded-md" />
        </div>
        <div className="space-y-2">
          <Skeleton className="h-9 w-9 mx-auto" />
          <Skeleton className="h-9 w-9 mx-auto" />
          <Skeleton className="h-9 w-9 mx-auto" />
        </div>
      </div>
    </div>
  );
}

function MobileSidebarSkeletonContent() {
  return (
    <div className="flex flex-col h-full w-full text-sidebar-foreground">
      <div className="h-[72px] flex items-center shrink-0 px-4">
        <div className="flex items-center gap-3 flex-1">
          <Skeleton className="h-10 w-10 rounded-full shrink-0" />
          <div className="space-y-1.5">
            <Skeleton className="h-4 w-24" />
            <Skeleton className="h-3 w-14" />
          </div>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto py-4 space-y-6 px-3">
        <div>
          <Skeleton className="h-10 w-full rounded-md" />
        </div>
        <div className="space-y-2">
          <Skeleton className="h-4 w-20" />
          <Skeleton className="h-9 w-full" />
          <Skeleton className="h-9 w-full" />
          <Skeleton className="h-9 w-full" />
        </div>
      </div>
    </div>
  );
}

/* ── Entry point ── */

function SidebarContent({
  skeleton = false,
  mobile = false,
  hovered = false,
  onNavigate,
  onLockHover,
  onUnlockHover,
}: AppSidebarProps) {
  if (skeleton && mobile) return <MobileSidebarSkeletonContent />;
  if (skeleton) return <SidebarSkeletonContent />;
  if (mobile) return <MobileSidebarLiveContent onNavigate={onNavigate} />;
  return (
    <DesktopSidebarLiveContent
      hovered={hovered}
      onLockHover={onLockHover}
      onUnlockHover={onUnlockHover}
    />
  );
}

/* ── Desktop: fixed w-14, labels portal to body on hover ── */

function DesktopSidebarLiveContent({
  hovered,
  onLockHover,
  onUnlockHover,
}: {
  hovered: boolean;
  onLockHover?: () => void;
  onUnlockHover?: () => void;
}) {
  const { role, roleLoading, preferredOffice, officeLoading, officeList, openSuggestions } =
    useSidebarData();
  const location = useLocation();
  const savePreferredOffice = useSavePreferredOffice();
  const navigate = useNavigate();
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);

  const canAccessAdmin = role === 'admin' || role === 'officeManager';
  const showSkeleton = !auth.currentUser;
  const showOfficeSelectorSkeleton = showSkeleton || officeLoading;

  const isActiveRoute = (path: string) =>
    location.pathname === path || location.pathname.startsWith(path + '/');

  const isBookingRoute = isActiveRoute('/booking');

  const handleOfficeChange = (officeId: string) => {
    if (auth.currentUser?.uid) {
      savePreferredOffice(auth.currentUser.uid, officeId);
    }
  };

  return (
    <div className="flex h-full w-14 flex-col text-sidebar-foreground">
      {/* Header - avatar with name/email always visible to the left */}
      <div className="h-[72px] flex items-center justify-center shrink-0 relative">
        {showSkeleton ? (
          <Skeleton className="h-8 w-8 rounded-full" />
        ) : (
          <>
            {auth.currentUser?.photoURL ? (
              <img
                src={auth.currentUser.photoURL}
                alt={auth.currentUser.displayName || 'Profile'}
                className="w-8 h-8 rounded-full"
              />
            ) : (
              <div className="w-8 h-8 rounded-full bg-sidebar-accent flex items-center justify-center">
                <IconUser className="w-4 h-4" />
              </div>
            )}
            <div className="absolute right-full top-1/2 -translate-y-1/2 mr-3 text-right whitespace-nowrap">
              <p className="text-sm font-semibold text-foreground">
                {auth.currentUser?.displayName || 'User'}
              </p>
              {auth.currentUser?.email && (
                <p className="text-xs text-muted-foreground">{auth.currentUser.email}</p>
              )}
            </div>
          </>
        )}
      </div>

      {/* Body. Wider than the rail and hung off its right edge, so each label
          sits in the same row as its icon and scrolls with it. */}
      <div className="relative min-h-0 flex-1">
        <div className="pointer-events-none absolute inset-y-0 right-0 w-72 space-y-6 overflow-x-hidden overflow-y-auto py-4">
          {/* Office Selector */}
          <div className={cn(!isBookingRoute && 'opacity-50')}>
            {showOfficeSelectorSkeleton ? (
              <div className="ml-auto flex w-14 justify-center">
                <Skeleton className="h-9 w-9 rounded-md" />
              </div>
            ) : (
              <DesktopOfficeSelector
                offices={officeList}
                preferredOffice={preferredOffice}
                onChangeOffice={handleOfficeChange}
                showLabel={hovered}
                disabled={!isBookingRoute}
                onLockHover={onLockHover}
                onUnlockHover={onUnlockHover}
              />
            )}
          </div>

          {/* Navigation */}
          {roleLoading ? (
            <div className="space-y-2">
              <div className="ml-auto flex w-14 justify-center">
                <Skeleton className="h-9 w-9" />
              </div>
              <div className="ml-auto flex w-14 justify-center">
                <Skeleton className="h-9 w-9" />
              </div>
              <div className="ml-auto flex w-14 justify-center">
                <Skeleton className="h-9 w-9" />
              </div>
            </div>
          ) : (
            <>
              <div className="space-y-1">
                <DesktopNavItem
                  to="/booking"
                  search={{
                    resourceId: undefined,
                    officeId: undefined,
                    date: undefined,
                    startTime: undefined,
                    endTime: undefined,
                  }}
                  icon={<IconCalendar className="w-5 h-5 shrink-0" />}
                  label="Booking"
                  active={isActiveRoute('/booking')}
                  showLabel={hovered}
                />
              </div>

              {canAccessAdmin && (
                <div className="space-y-1">
                  <DesktopNavItem
                    to="/admin/offices"
                    icon={<IconBuilding className="w-5 h-5 shrink-0" />}
                    label="Offices"
                    active={isActiveRoute('/admin/offices')}
                    showLabel={hovered}
                  />
                  {role === 'admin' && (
                    <DesktopNavItem
                      to="/admin/users"
                      icon={<IconUsers className="w-5 h-5 shrink-0" />}
                      label="Users"
                      active={isActiveRoute('/admin/users')}
                      showLabel={hovered}
                    />
                  )}
                  <DesktopNavItem
                    to="/admin/events"
                    icon={<IconCalendarEvent className="w-5 h-5 shrink-0" />}
                    label="Events"
                    active={isActiveRoute('/admin/events')}
                    showLabel={hovered}
                  />
                  <DesktopNavItem
                    to="/admin/suggestions"
                    icon={<IconMessage2 className="w-5 h-5 shrink-0" />}
                    label="Suggestions"
                    badge={openSuggestions}
                    active={isActiveRoute('/admin/suggestions')}
                    showLabel={hovered}
                  />
                </div>
              )}

              <div className="space-y-1">
                <DesktopNavItem
                  to="/profile"
                  search={{ settings: undefined }}
                  icon={<IconUser className="w-5 h-5 shrink-0" />}
                  label="Profile"
                  active={isActiveRoute('/profile')}
                  showLabel={hovered}
                />
                <DesktopNavItem
                  to="/stats"
                  icon={<IconChartBar className="w-5 h-5 shrink-0" />}
                  label="Stats"
                  active={isActiveRoute('/stats')}
                  showLabel={hovered}
                />
                <DesktopNavItem
                  to="/changelog"
                  icon={<Sparkles className="w-5 h-5 shrink-0" />}
                  label="What's New"
                  active={isActiveRoute('/changelog')}
                  showLabel={hovered}
                />
              </div>

              <div className="space-y-1">
                <DesktopThemePicker
                  showLabel={hovered}
                  onLockHover={onLockHover}
                  onUnlockHover={onUnlockHover}
                />
              </div>

              <div className="space-y-1">
                <DesktopIconButton
                  icon={<LogOut className="w-5 h-5 shrink-0" />}
                  label="Log out"
                  onClick={() => setShowLogoutConfirm(true)}
                  showLabel={hovered}
                />
              </div>

              <ConfirmDialog
                open={showLogoutConfirm}
                onOpenChange={() => setShowLogoutConfirm(false)}
                title="Log out"
                description="Are you sure you want to log out?"
                confirmLabel="Log out"
                cancelLabel="Cancel"
                variant="destructive"
                onConfirm={() => {
                  setShowLogoutConfirm(false);
                  navigate({ to: '/logout' });
                }}
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/* ── Desktop nav item. The label is in the row, so it scrolls with the icon. ── */

function DesktopNavItem({
  to,
  search,
  icon,
  label,
  badge = 0,
  active,
  showLabel,
}: {
  to: string;
  search?: Record<string, unknown>;
  icon: React.ReactNode;
  label: string;
  badge?: number;
  active?: boolean;
  showLabel: boolean;
}) {
  const accessibleName = badge > 0 ? `${label}, ${badge} open` : label;

  return (
    <Link
      to={to}
      search={search as never}
      aria-label={accessibleName}
      title={accessibleName}
      className={railRowClass({ active })}
    >
      <RailLabel show={showLabel}>{label}</RailLabel>
      <RailIcon>
        <span className="relative inline-flex">
          {icon}
          <SuggestionCountBadge count={badge} />
        </span>
      </RailIcon>
    </Link>
  );
}

function DesktopIconButton({
  icon,
  label,
  onClick,
  showLabel,
}: {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  showLabel: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      title={label}
      className={railRowClass({})}
    >
      <RailLabel show={showLabel}>{label}</RailLabel>
      <RailIcon>{icon}</RailIcon>
    </button>
  );
}

/* ── Desktop theme picker with animated toggle group ── */

const THEME_ICON: Record<ThemePreference, typeof Sun> = {
  light: Sun,
  dark: Moon,
  system: IconBrightnessAuto as unknown as typeof Sun,
};

function DesktopThemePicker({
  showLabel,
  onLockHover,
  onUnlockHover,
}: {
  showLabel: boolean;
  onLockHover?: () => void;
  onUnlockHover?: () => void;
}) {
  const { theme, setTheme } = useTheme();
  const [expanded, setExpanded] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const expandedRef = useRef(false);
  const Icon = THEME_ICON[theme];

  const toggle = () => {
    const next = !expandedRef.current;
    expandedRef.current = next;
    setExpanded(next);
    if (next) onLockHover?.();
    else onUnlockHover?.();
  };

  const closeIfOpen = () => {
    if (expandedRef.current) {
      expandedRef.current = false;
      setExpanded(false);
      onUnlockHover?.();
    }
  };

  useEffect(() => {
    if (!expanded) return;
    const handlePointerDown = (e: MouseEvent) => {
      if (ref.current?.contains(e.target as Node)) return;
      closeIfOpen();
    };
    document.addEventListener('pointerdown', handlePointerDown);
    return () => document.removeEventListener('pointerdown', handlePointerDown);
  }, [expanded]);

  const handleChange = (value: string) => {
    if (value) {
      setTheme(value as ThemePreference);
      closeIfOpen();
    }
  };

  return (
    <div ref={ref} className="pointer-events-auto ml-auto flex w-max items-center">
      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ width: 0, opacity: 0 }}
            animate={{ width: 'auto', opacity: 1 }}
            exit={{ width: 0, opacity: 0 }}
            transition={{ type: 'spring', bounce: 0, stiffness: 300, damping: 30 }}
            className="overflow-hidden"
          >
            <ToggleGroup
              type="single"
              value={theme}
              onValueChange={handleChange}
              size="sm"
              className="mr-1 bg-gray-200 p-0.5 backdrop-blur-sm dark:bg-sidebar-accent/80 rounded-md"
            >
              <ToggleGroupItem value="light" aria-label="Light mode" title="Light mode">
                <Sun className="w-4 h-4" />
              </ToggleGroupItem>
              <ToggleGroupItem value="dark" aria-label="Dark mode" title="Dark mode">
                <Moon className="w-4 h-4" />
              </ToggleGroupItem>
              <ToggleGroupItem value="system" aria-label="Auto mode" title="Auto mode">
                <IconBrightnessAuto className="w-4 h-4" />
              </ToggleGroupItem>
            </ToggleGroup>
          </motion.div>
        )}
      </AnimatePresence>
      <button
        type="button"
        onClick={toggle}
        aria-label="Theme"
        title="Theme"
        className={railRowClass({ className: 'ml-0' })}
      >
        {!expanded && <RailLabel show={showLabel}>Theme</RailLabel>}
        <RailIcon>
          <Icon className="w-5 h-5 shrink-0" />
        </RailIcon>
      </button>
    </div>
  );
}

/* ── Mobile theme picker ── */

function MobileThemePicker() {
  const { theme, setTheme } = useTheme();

  return (
    <ToggleGroup
      type="single"
      value={theme}
      onValueChange={(v: string) => {
        if (v) setTheme(v as ThemePreference);
      }}
      size="default"
      className="bg-gray-200 dark:bg-sidebar-accent/40 rounded-lg p-1"
    >
      <ToggleGroupItem value="light" aria-label="Light mode" title="Light mode">
        <Sun className="w-6 h-6" />
      </ToggleGroupItem>
      <ToggleGroupItem value="dark" aria-label="Dark mode" title="Dark mode">
        <Moon className="w-6 h-6" />
      </ToggleGroupItem>
      <ToggleGroupItem value="system" aria-label="Auto mode" title="Auto mode">
        <IconBrightnessAuto className="w-6 h-6" />
      </ToggleGroupItem>
    </ToggleGroup>
  );
}

/* ── Mobile office picker (styled as nav button) ── */

function MobileOfficePicker({
  offices,
  preferredOffice,
  onChangeOffice,
  disabled,
  onNavigate,
}: {
  offices: Office[];
  preferredOffice: string | null;
  onChangeOffice: (officeId: string) => void;
  disabled?: boolean;
  onNavigate?: () => void;
}) {
  const officeList = offices;

  const enabledOffices = useMemo(() => {
    if (!officeList) return [];
    return officeList.filter(o => !o.disabled);
  }, [officeList]);

  const officesByCountry = useMemo(() => {
    const grouped: Record<string, typeof enabledOffices> = {};
    enabledOffices.forEach(office => {
      const country = office.country || 'Other';
      if (!grouped[country]) grouped[country] = [];
      grouped[country].push(office);
    });
    Object.values(grouped).forEach(list =>
      list.sort((a, b) =>
        a.city !== b.city ? a.city.localeCompare(b.city) : a.name.localeCompare(b.name)
      )
    );
    return grouped;
  }, [enabledOffices]);

  const sortedCountries = useMemo(() => Object.keys(officesByCountry).sort(), [officesByCountry]);

  const triggerRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);

  return (
    <Select
      onValueChange={v => {
        onChangeOffice(v);
        onNavigate?.();
      }}
      value={preferredOffice || undefined}
      disabled={disabled}
      onOpenChange={setOpen}
    >
      <SelectTrigger ref={triggerRef} className="sr-only" tabIndex={-1} aria-hidden>
        <span />
      </SelectTrigger>
      <button
        type="button"
        disabled={disabled}
        onClick={() => triggerRef.current?.click()}
        className={cn(
          'flex items-center gap-3 rounded-xl px-4 py-3 text-base transition-colors ml-auto w-fit',
          open
            ? 'bg-gray-200 dark:bg-sidebar-accent text-sidebar-foreground'
            : 'text-sidebar-foreground/70 hover:text-sidebar-foreground hover:bg-gray-200/70 dark:hover:bg-sidebar-accent',
          disabled && 'cursor-not-allowed'
        )}
      >
        <span>Switch Office</span>
        <IconMapPin className="w-5 h-5 shrink-0" />
      </button>
      <SelectContent className="min-w-56 text-base [&_[role=option]]:py-2.5 [&_[role=option]]:text-base">
        {sortedCountries.map(country => (
          <SelectGroup key={country}>
            <SelectLabel className="text-sm">{country}</SelectLabel>
            {officesByCountry[country].map(office => (
              <SelectItem key={office.id} value={office.id}>
                {office.name} - {office.city}
              </SelectItem>
            ))}
          </SelectGroup>
        ))}
      </SelectContent>
    </Select>
  );
}

/* ── Desktop office selector with floating label ── */

function DesktopOfficeSelector({
  offices,
  preferredOffice,
  onChangeOffice,
  showLabel,
  disabled,
  onLockHover,
  onUnlockHover,
}: {
  offices: Office[];
  preferredOffice: string | null;
  onChangeOffice: (officeId: string) => void;
  showLabel: boolean;
  disabled?: boolean;
  onLockHover?: () => void;
  onUnlockHover?: () => void;
}) {
  const officeList = offices;

  const enabledOffices = useMemo(() => {
    if (!officeList) return [];
    return officeList.filter(o => !o.disabled);
  }, [officeList]);

  const officesByCountry = useMemo(() => {
    const grouped: Record<string, typeof enabledOffices> = {};
    enabledOffices.forEach(office => {
      const country = office.country || 'Other';
      if (!grouped[country]) grouped[country] = [];
      grouped[country].push(office);
    });
    Object.values(grouped).forEach(list =>
      list.sort((a, b) =>
        a.city !== b.city ? a.city.localeCompare(b.city) : a.name.localeCompare(b.name)
      )
    );
    return grouped;
  }, [enabledOffices]);

  const sortedCountries = useMemo(() => Object.keys(officesByCountry).sort(), [officesByCountry]);

  const triggerRef = useRef<HTMLButtonElement>(null);
  const [selectOpen, setSelectOpen] = useState(false);
  const labelsVisible = showLabel && !disabled;

  return (
    <div>
      <Select
        open={selectOpen}
        onValueChange={onChangeOffice}
        value={preferredOffice || undefined}
        disabled={disabled}
        onOpenChange={open => {
          setSelectOpen(open);
          if (open) onLockHover?.();
          else onUnlockHover?.();
        }}
      >
        <SelectTrigger
          ref={triggerRef}
          aria-label="Switch office"
          title="Switch office"
          className={cn(
            railRowClass({ open: selectOpen }),
            !selectOpen && 'bg-transparent dark:bg-transparent',
            'h-auto gap-0 border-0 px-0 shadow-none focus:ring-0 focus-visible:ring-0 [&>svg:last-child]:hidden'
          )}
        >
          <RailLabel show={labelsVisible}>Switch office</RailLabel>
          <RailIcon>
            <IconMapPin className="w-5 h-5" />
          </RailIcon>
        </SelectTrigger>
        <SelectContent side="left" align="start">
          {sortedCountries.map(country => (
            <SelectGroup key={country}>
              <SelectLabel>{country}</SelectLabel>
              {officesByCountry[country].map(office => (
                <SelectItem key={office.id} value={office.id}>
                  {office.name} - {office.city}
                </SelectItem>
              ))}
            </SelectGroup>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

/* ── Mobile: always fully expanded, unchanged ── */

function MobileSidebarLiveContent({ onNavigate }: { onNavigate?: () => void }) {
  const { role, roleLoading, preferredOffice, officeLoading, officeList, openSuggestions } =
    useSidebarData();
  const location = useLocation();
  const savePreferredOffice = useSavePreferredOffice();
  const navigate = useNavigate();
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);

  const canAccessAdmin = role === 'admin' || role === 'officeManager';
  const showSkeleton = !auth.currentUser;
  const showOfficeSelectorSkeleton = showSkeleton || officeLoading;

  const isActiveRoute = (path: string) =>
    location.pathname === path || location.pathname.startsWith(path + '/');

  const isBookingRoute = isActiveRoute('/booking');

  const handleOfficeChange = (officeId: string) => {
    if (auth.currentUser?.uid) {
      savePreferredOffice(auth.currentUser.uid, officeId);
    }
  };

  return (
    <div className="flex flex-col h-full w-full text-sidebar-foreground">
      <div className="h-[72px] flex items-center shrink-0 px-4">
        {showSkeleton ? (
          <div className="flex items-center gap-3 flex-1 flex-row-reverse">
            <Skeleton className="h-10 w-10 rounded-full shrink-0" />
            <div className="space-y-1.5 text-right">
              <Skeleton className="h-4 w-24 ml-auto" />
              <Skeleton className="h-3 w-14 ml-auto" />
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-3 min-w-0 flex-row-reverse">
            {auth.currentUser?.photoURL ? (
              <img
                src={auth.currentUser.photoURL}
                alt={auth.currentUser.displayName || 'Profile'}
                className="w-10 h-10 rounded-full shrink-0"
              />
            ) : (
              <div className="w-10 h-10 rounded-full bg-sidebar-accent flex items-center justify-center shrink-0">
                <IconUser className="w-5 h-5" />
              </div>
            )}
            <div className="min-w-0 text-right">
              <p className="text-sm font-semibold truncate">
                {auth.currentUser?.displayName || 'User'}
              </p>
              {auth.currentUser?.email && (
                <p className="text-xs text-sidebar-foreground/60 truncate">
                  {auth.currentUser.email}
                </p>
              )}
            </div>
          </div>
        )}
      </div>

      <div className="flex-1 overflow-y-auto py-4 space-y-6 px-3">
        {roleLoading ? (
          <div className="space-y-2">
            <Skeleton className="h-4 w-20" />
            <Skeleton className="h-9 w-full" />
            <Skeleton className="h-9 w-full" />
            <Skeleton className="h-9 w-full" />
          </div>
        ) : (
          <>
            {canAccessAdmin ? (
              <>
                <div className="space-y-1">
                  <div className={cn(!isBookingRoute && 'opacity-50')}>
                    {!showOfficeSelectorSkeleton && (
                      <MobileOfficePicker
                        offices={officeList}
                        preferredOffice={preferredOffice}
                        onChangeOffice={handleOfficeChange}
                        disabled={!isBookingRoute}
                        onNavigate={onNavigate}
                      />
                    )}
                  </div>
                  <MobileNavItem
                    to="/booking"
                    search={{
                      resourceId: undefined,
                      officeId: undefined,
                      date: undefined,
                      startTime: undefined,
                      endTime: undefined,
                    }}
                    icon={<IconCalendar className="w-5 h-5 shrink-0" />}
                    label="Booking"
                    active={isActiveRoute('/booking')}
                    onClick={onNavigate}
                  />
                </div>

                <div className="space-y-1">
                  <MobileNavItem
                    to="/admin/offices"
                    icon={<IconBuilding className="w-5 h-5 shrink-0" />}
                    label="Offices"
                    active={isActiveRoute('/admin/offices')}
                    onClick={onNavigate}
                  />
                  {role === 'admin' && (
                    <MobileNavItem
                      to="/admin/users"
                      icon={<IconUsers className="w-5 h-5 shrink-0" />}
                      label="Users"
                      active={isActiveRoute('/admin/users')}
                      onClick={onNavigate}
                    />
                  )}
                  <MobileNavItem
                    to="/admin/events"
                    icon={<IconCalendarEvent className="w-5 h-5 shrink-0" />}
                    label="Events"
                    active={isActiveRoute('/admin/events')}
                    onClick={onNavigate}
                  />
                  <MobileNavItem
                    to="/admin/suggestions"
                    icon={<IconMessage2 className="w-5 h-5 shrink-0" />}
                    label="Suggestions"
                    badge={openSuggestions}
                    active={isActiveRoute('/admin/suggestions')}
                    onClick={onNavigate}
                  />
                </div>

                <div className="space-y-1">
                  <MobileNavItem
                    to="/profile"
                    search={{ settings: undefined }}
                    icon={<IconUser className="w-5 h-5 shrink-0" />}
                    label="Profile"
                    active={isActiveRoute('/profile')}
                    onClick={onNavigate}
                  />
                  <MobileNavItem
                    to="/stats"
                    icon={<IconChartBar className="w-5 h-5 shrink-0" />}
                    label="Stats"
                    active={isActiveRoute('/stats')}
                    onClick={onNavigate}
                  />
                  <MobileNavItem
                    to="/changelog"
                    icon={<Sparkles className="w-5 h-5 shrink-0" />}
                    label="What's New"
                    active={isActiveRoute('/changelog')}
                    onClick={onNavigate}
                  />
                </div>
              </>
            ) : (
              <div className="space-y-1">
                <div className={cn(!isBookingRoute && 'opacity-50')}>
                  {!showOfficeSelectorSkeleton && (
                    <MobileOfficePicker
                      offices={officeList}
                      preferredOffice={preferredOffice}
                      onChangeOffice={handleOfficeChange}
                      disabled={!isBookingRoute}
                      onNavigate={onNavigate}
                    />
                  )}
                </div>
                <MobileNavItem
                  to="/booking"
                  search={{
                    resourceId: undefined,
                    officeId: undefined,
                    date: undefined,
                    startTime: undefined,
                    endTime: undefined,
                  }}
                  icon={<IconCalendar className="w-5 h-5 shrink-0" />}
                  label="Booking"
                  active={isActiveRoute('/booking')}
                  onClick={onNavigate}
                />
                <MobileNavItem
                  to="/profile"
                  search={{ settings: undefined }}
                  icon={<IconUser className="w-5 h-5 shrink-0" />}
                  label="Profile"
                  active={isActiveRoute('/profile')}
                  onClick={onNavigate}
                />
                <MobileNavItem
                  to="/stats"
                  icon={<IconChartBar className="w-5 h-5 shrink-0" />}
                  label="Stats"
                  active={isActiveRoute('/stats')}
                  onClick={onNavigate}
                />
                <MobileNavItem
                  to="/changelog"
                  icon={<Sparkles className="w-5 h-5 shrink-0" />}
                  label="What's New"
                  active={isActiveRoute('/changelog')}
                  onClick={onNavigate}
                />
              </div>
            )}

            <ConfirmDialog
              open={showLogoutConfirm}
              onOpenChange={() => setShowLogoutConfirm(false)}
              title="Log out"
              description="Are you sure you want to log out?"
              confirmLabel="Log out"
              cancelLabel="Cancel"
              variant="destructive"
              onConfirm={() => {
                setShowLogoutConfirm(false);
                onNavigate?.();
                navigate({ to: '/logout' });
              }}
            />
          </>
        )}
      </div>

      <div className="shrink-0 px-3 pb-2 flex justify-end">
        <MobileThemePicker />
      </div>

      <div className="shrink-0 flex items-center justify-end gap-2 px-3 py-3 border-t border-sidebar-border">
        <button
          onClick={() => setShowLogoutConfirm(true)}
          className="flex items-center gap-2 rounded-md px-3 py-2 text-sm text-sidebar-foreground/70 hover:text-sidebar-foreground hover:bg-gray-200/70 dark:hover:bg-sidebar-accent transition-colors"
        >
          <LogOut className="w-4 h-4 shrink-0" />
          <span>Log out</span>
        </button>
      </div>
    </div>
  );
}

function MobileNavItem({
  to,
  search,
  icon,
  label,
  badge = 0,
  active,
  onClick,
}: {
  to: string;
  search?: Record<string, unknown>;
  icon: React.ReactNode;
  label: string;
  badge?: number;
  active?: boolean;
  onClick?: () => void;
}) {
  const accessibleName = badge > 0 ? `${label}, ${badge} open` : label;

  return (
    <Link
      to={to}
      search={search as never}
      onClick={onClick}
      aria-label={accessibleName}
      title={accessibleName}
      className="block"
    >
      <div
        className={cn(
          'flex items-center gap-3 rounded-xl px-4 py-3 text-base transition-colors ml-auto w-fit',
          active
            ? 'bg-gray-200 dark:bg-sidebar-accent text-sidebar-accent-foreground font-medium'
            : 'text-sidebar-foreground/70 hover:text-sidebar-foreground hover:bg-gray-200/70 dark:hover:bg-sidebar-accent'
        )}
      >
        <span>{label}</span>
        <span className="relative inline-flex">
          {icon}
          <SuggestionCountBadge count={badge} />
        </span>
      </div>
    </Link>
  );
}

export { SidebarContent };
export type { AppSidebarProps };
